import '../reports-hub/style.scss';
import './style.scss';

import { type CSSProperties, useEffect, useState } from 'react';

import { useGlobalContext } from '../../../context/global';
import { SUPPORTER_REPORT_FILED } from '../../../hooks/useSupporterHome';
import {
	INCIDENT_CATEGORIES,
	INCIDENT_SEVERITIES,
	type IncidentCategory,
	type IncidentSeverity,
	MAX_INCIDENT_DESCRIPTION,
	MAX_INCIDENT_PHOTOS,
	MAX_INCIDENT_TITLE,
} from '../../../interface/vesr';
import { TypeButton } from '../../utils/button';
import { TypeInput } from '../../utils/input';
import { MultipleMediaUploadComponent } from '../../utils/multiple-upload-document';
import { CheckSVG } from '../../utils/svgs';
import { TypeTextArea } from '../../utils/text-area';
import { displayName } from '../overview/observer-overview';
import { CategoryGlyph, PinGlyph } from '../reports-hub/icons';
import { useIncidentSubmit } from '../reports-hub/submit-machine';
import { composeTitle } from '../reports-hub/utils';
import type { ISupporterReportData } from './SupporterHome';
import { buildSupporterIncidentRequest } from './utils';

interface Coords {
	latitude: number;
	longitude: number;
	accuracy: number;
}
type LocationState = { kind: 'off' } | { kind: 'finding' } | { kind: 'on'; coords: Coords } | { kind: 'error'; message: string };

/**
 * An Independent Supporter's incident report: one screen, not the agent wizard.
 *
 * <p>A supporter has no posting, so nothing is auto-tagged and location is theirs to offer, not a
 * requirement. The report always names the election selected on the home (`electionId`), which
 * api.vesr checks against the token. The create and the photo uploads reuse the agent app's submit
 * machine (idempotent create, per-photo retry), and whatever api.vesr says when it refuses — a 403
 * or a 400 — is shown in its own words.</p>
 */
const SupporterReportPanel = () => {
	const { rsProps } = useGlobalContext();
	const data = (rsProps?.data ?? null) as ISupporterReportData | null;
	const electionId = data?.electionId ?? null;

	const [category, setCategory] = useState<IncidentCategory | null>(null);
	const [severity, setSeverity] = useState<IncidentSeverity>('Medium');
	const [note, setNote] = useState('');
	const [title, setTitle] = useState('');
	const [titleTouched, setTitleTouched] = useState(false);
	const [files, setFiles] = useState<File[]>([]);
	const [location, setLocation] = useState<LocationState>({ kind: 'off' });

	const machine = useIncidentSubmit();
	const submitting = machine.phase === 'creating' || machine.phase === 'uploading';

	useEffect(() => {
		if (!titleTouched && category) setTitle(composeTitle(category, severity, note));
	}, [category, severity, note, titleTouched]);

	useEffect(() => {
		if (machine.phase === 'done') window.dispatchEvent(new Event(SUPPORTER_REPORT_FILED));
	}, [machine.phase]);

	const shareLocation = () => {
		if (!navigator.geolocation) {
			setLocation({ kind: 'error', message: 'This browser cannot share a location.' });
			return;
		}
		setLocation({ kind: 'finding' });
		navigator.geolocation.getCurrentPosition(
			(p) =>
				setLocation({
					kind: 'on',
					coords: { latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy },
				}),
			(err) =>
				setLocation({
					kind: 'error',
					message:
						err.code === err.PERMISSION_DENIED
							? 'Location is blocked for this site. You can still send the report without it.'
							: 'Your location could not be found. You can still send the report without it.',
				}),
			{ enableHighAccuracy: true, timeout: 20_000, maximumAge: 60_000 }
		);
	};

	const coords = location.kind === 'on' ? location.coords : null;
	const canSend = !!electionId && !!category && !!title.trim() && !submitting;

	const onSend = () => {
		if (!canSend || !electionId || !category) return;
		void machine.submit({
			request: buildSupporterIncidentRequest(electionId, { title, note, category, severity, coords }),
			files,
			video: null,
			coords,
			ctx: { electionId, electionName: data?.electionName },
			description: note,
		});
	};

	const reportAnother = () => {
		machine.reset();
		setCategory(null);
		setSeverity('Medium');
		setNote('');
		setTitle('');
		setTitleTouched(false);
		setFiles([]);
	};

	if (!electionId) {
		return (
			<div className='supporter-report'>
				<div className='sh-note sh-note--warn'>
					<p className='sh-note__text'>Choose an election on your home screen, then report from there.</p>
				</div>
			</div>
		);
	}

	if (machine.phase === 'done' && machine.incident) {
		return (
			<div className='rh-receipt animate-fade-in'>
				<span className='rh-receipt__icon'>
					<CheckSVG
						width='30'
						height='30'
						color='var(--success-600)'
					/>
				</span>
				<h3 className='rh-receipt__title'>Report sent</h3>
				<p className='rh-receipt__hint'>The situation room has it. Follow its progress under My reports.</p>
				<div className='rh-receipt__ref-wrap'>
					<code className='rh-receipt__ref'>#{machine.incident.referenceId}</code>
				</div>
				{machine.failedFiles.length ? (
					<div className='rh-banner rh-banner--warning'>
						<p className='rh-banner__text'>
							{machine.failedFiles.length} photo{machine.failedFiles.length > 1 ? 's' : ''} didn’t upload. Your report is safe —
							retry just the photos.
						</p>
						<TypeButton
							title='Retry photos'
							buttonSize='small'
							buttonType='outlined'
							onClick={() => void machine.retryPhotos()}
						/>
					</div>
				) : null}
				{machine.rejectedFiles.length ? (
					<div className='rh-banner rh-banner--warning'>
						<p className='rh-banner__text'>
							{machine.rejectedFiles.length === 1 ? 'One photo was' : `${machine.rejectedFiles.length} photos were`} too large to
							attach.
						</p>
					</div>
				) : null}
				<div className='rh-receipt__ctas'>
					<TypeButton
						title='Back to home'
						buttonSize='small'
						className='w-100'
						onClick={() => rsProps?.closeSection()}
					/>
					<TypeButton
						title='Report another'
						buttonSize='small'
						buttonType='outlined'
						className='w-100'
						onClick={reportAnother}
					/>
				</div>
			</div>
		);
	}

	return (
		<div className='supporter-report rh-new'>
			<p className='sr-election'>
				<span className='sr-election__label'>Reporting in</span>
				<span className='sr-election__name'>{displayName(data?.electionName) || `Election ${electionId}`}</span>
			</p>

			<div className='rh-step'>
				<h4
					className='rh-step__title'
					id='sr-category'
				>
					What’s happening?
				</h4>
				<div
					className='rh-cat-grid'
					role='radiogroup'
					aria-labelledby='sr-category'
				>
					{INCIDENT_CATEGORIES.map((cat) => (
						<button
							key={cat.value}
							type='button'
							className='rh-cat-card'
							style={{ '--accent': cat.accent } as CSSProperties}
							role='radio'
							aria-checked={category === cat.value}
							onClick={() => setCategory(cat.value)}
						>
							<span
								className='rh-cat-card__icon'
								aria-hidden='true'
							>
								<CategoryGlyph category={cat.value} />
							</span>
							<span className='rh-cat-card__title'>{cat.label}</span>
							<span className='rh-cat-card__desc'>{cat.helper}</span>
						</button>
					))}
				</div>
			</div>

			<div className='rh-step'>
				<h4
					className='rh-step__title'
					id='sr-severity'
				>
					How serious is it?
				</h4>
				<div
					className='rh-sev-ramp'
					role='radiogroup'
					aria-labelledby='sr-severity'
				>
					{INCIDENT_SEVERITIES.map((sev) => (
						<button
							key={sev.value}
							type='button'
							role='radio'
							aria-checked={severity === sev.value}
							className={`rh-sev-pill rh-sev-pill--${sev.value.toLowerCase()} ${severity === sev.value ? 'is-selected' : ''}`}
							style={{ '--sev': sev.color, '--sev-bg': sev.bg } as CSSProperties}
							onClick={() => setSeverity(sev.value)}
						>
							<span className='rh-sev-pill__label'>{sev.label}</span>
							<span className='rh-sev-pill__helper'>{sev.helper}</span>
						</button>
					))}
				</div>
			</div>

			<div className='rh-field rh-field--note'>
				<TypeTextArea
					label='What did you see? (optional)'
					placeholder='Where it happened, who was involved, anything that helps the situation room act…'
					value={note}
					onChange={(e) => {
						if (e.target.value.length > MAX_INCIDENT_DESCRIPTION) return;
						setNote(e.target.value);
					}}
				/>
				<span className='rh-field__count'>
					{note.length}/{MAX_INCIDENT_DESCRIPTION}
				</span>
			</div>

			<TypeInput
				label='Report title'
				value={title}
				maxLength={MAX_INCIDENT_TITLE}
				placeholder='Pick a category and we’ll suggest one'
				onChange={(e) => {
					setTitleTouched(true);
					setTitle(e.target.value);
				}}
			/>

			<div className='rh-field'>
				<p className='rh-field__label'>Photos (optional)</p>
				<MultipleMediaUploadComponent
					setFiles={(next) => setFiles(next.slice(0, MAX_INCIDENT_PHOTOS))}
					files={files}
					uploadedFiles={[]}
					load={machine.phase === 'uploading'}
					fileStatuses={machine.fileStatuses}
					captureTitle='Add photos'
					captureSubtitle='Only what you saw yourself'
				/>
			</div>

			<div className={`sr-location sr-location--${location.kind}`}>
				<PinGlyph />
				<div className='sr-location__text'>
					{location.kind === 'on' ? (
						<>
							<span>
								Location added · {location.coords.latitude.toFixed(5)}, {location.coords.longitude.toFixed(5)}
							</span>
							<span className='sr-location__sub'>Sent with this report only.</span>
						</>
					) : location.kind === 'finding' ? (
						<span>Finding your location…</span>
					) : location.kind === 'error' ? (
						<span>{location.message}</span>
					) : (
						<>
							<span>Location (optional)</span>
							<span className='sr-location__sub'>Helps the situation room place the incident.</span>
						</>
					)}
				</div>
				{location.kind === 'on' ? (
					<button
						type='button'
						className='sr-location__btn'
						onClick={() => setLocation({ kind: 'off' })}
					>
						Remove
					</button>
				) : location.kind !== 'finding' ? (
					<button
						type='button'
						className='sr-location__btn'
						onClick={shareLocation}
					>
						{location.kind === 'error' ? 'Try again' : 'Add my location'}
					</button>
				) : null}
			</div>

			<div className='rh-sticky-cta'>
				{/* Beside the button: a refusal must be on screen where Send was pressed. */}
				{machine.createError ? (
					<div
						className={`rh-banner ${machine.createError.offline ? 'rh-banner--offline' : 'rh-banner--warning'}`}
						role='alert'
					>
						<p className='rh-banner__text'>{machine.createError.message}</p>
					</div>
				) : null}

				<TypeButton
					title={machine.createError && !submitting ? 'Try again' : 'Send report'}
					className='w-100'
					buttonSize='small'
					buttonType={canSend ? 'danger' : 'disabled'}
					load={submitting}
					onClick={onSend}
				/>
				{!category ? <p className='rh-field__hint'>Pick what’s happening to send your report.</p> : null}
			</div>
		</div>
	);
};

export default SupporterReportPanel;
